/**
 * Apex feature API (`GET user/v1/featureStatus/.../Apex`) and credit-summary `Apex` semantics:
 * - `0` / `false` — legacy MUB / not on Apex PAYG program
 * - `1` / `true` — LMS + Apex invite (Activate Pay As You Go promo path)
 * - UTC epoch (ms or s) — user purchased MUB under Apex → PAYG post-checkout manage UI
 */

function readApexRawFromObject(obj) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  const keys = ['Apex', 'apex', 'APEX', 'featureApex', 'apexEnabled'];
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

/**
 * Pull raw `details.Apex` (or nested equivalents) from a feature-status or credit-summary payload.
 * @param {*} data
 * @returns {*}
 */
export function extractRawApexFromPayload(data) {
  if (data == null) return undefined;
  return (
    readApexRawFromObject(data?.details) ??
    readApexRawFromObject(data?.response?.details) ??
    readApexRawFromObject(data?.result?.details) ??
    readApexRawFromObject(data?.data?.details) ??
    (typeof data?.Apex !== 'undefined' && data?.Apex !== null && data?.Apex !== '' ? data.Apex : undefined) ??
    (typeof data?.apex !== 'undefined' && data?.apex !== null && data?.apex !== '' ? data.apex : undefined)
  );
}

/**
 * True when Apex is the PAYG program invite flag (`1` / `true`) from credit summary or feature API.
 * With MUB on credit summary, user is on Pay As You Go (not legacy MUB where Apex is `0`).
 * @param {*} apexValue
 * @returns {boolean}
 */
export function isApexPaygInviteFlag(apexValue) {
  return apexValue === 1 || apexValue === '1' || apexValue === true;
}

/**
 * True when Apex field is a purchase timestamp (MUB bought on Apex PAYG), not 0/1 flags.
 * Accepts ms (≥ 1e12) or seconds (1e9–1e11) in a realistic range.
 * @param {*} apexValue
 * @returns {boolean}
 */
export function isApexPaygMubPurchaseTimestamp(apexValue) {
  if (apexValue === null || apexValue === undefined || apexValue === '') return false;
  if (apexValue === true || apexValue === false) return false;
  if (apexValue === 1 || apexValue === 0 || apexValue === '1' || apexValue === '0') return false;

  let n;
  if (typeof apexValue === 'string') {
    const s = apexValue.trim();
    if (s === '') return false;
    n = Number(s);
  } else {
    n = Number(apexValue);
  }
  if (!Number.isFinite(n) || n <= 1) return false;

  // Milliseconds since epoch (e.g. 1781271856703)
  if (n >= 1_000_000_000_000) return true;
  // Seconds since epoch (~2001–2286)
  if (n >= 1_000_000_000 && n < 10_000_000_000) return true;

  return false;
}

/**
 * @param {*} data - API body (e.g. axios response.data for feature status)
 * @returns {{ rawApexValue: *, isLmsApexInviteActive: boolean, isLegacyApexOff: boolean, isPaygMubPurchased: boolean }}
 */
export function interpretApexFeatureResponse(data) {
  const rawApexValue = extractRawApexFromPayload(data);
  const isLegacyApexOff =
    rawApexValue === 0 || rawApexValue === '0' || rawApexValue === false;
  const isLmsApexInviteActive = isApexPaygInviteFlag(rawApexValue);
  const isPaygMubPurchased = isApexPaygMubPurchaseTimestamp(rawApexValue);

  return {
    rawApexValue,
    isLmsApexInviteActive,
    isLegacyApexOff,
    isPaygMubPurchased
  };
}

/**
 * Legacy boolean parse: `true` only for LMS Apex invite (Apex === 1).
 * @param {*} data
 * @returns {boolean|null} true / false / null if unrecognized
 */
export function parseApexFeatureStatusPayload(data) {
  const { isLmsApexInviteActive, isLegacyApexOff, isPaygMubPurchased, rawApexValue } =
    interpretApexFeatureResponse(data);

  if (isLmsApexInviteActive) return true;
  if (isLegacyApexOff) return false;
  if (isPaygMubPurchased) return false;
  if (rawApexValue === undefined || rawApexValue === null) return null;
  return null;
}
